import { effectiveEdgeOwners } from "@sooth-labs/idl";

// Multi-value ("multiway") forecast mode — the `MW:` chat-console trigger.
//
// A user opts a single prompt into multi-value forecasting by prefixing it with `MW:`
// (case-insensitive, optional space). When the `chatboard-multiway` flag is on, such a
// prompt is routed through the multi-value system: the contract server authors an
// outcome space (categorical or numeric buckets), the forecaster returns a PMF over
// those buckets, and the report renders a per-bucket breakdown instead of the P(YES)
// pie. Without the prefix (or with the flag off), everything takes the normal binary
// path unchanged — the prefix has no special meaning.
//
// Pure module (no React) — imported by the submit command, the chat-intent entry point,
// and the forecast client.
//
// The client no longer targets a dedicated MW: forecaster. A multi-value request runs on
// the engine the user chose — the forecast server refuses, before any spend, a lane that
// cannot answer with a distribution — so the run body differs from a binary one only in
// the answer-space fields it carries (`outcome_space` top-level and on the contract; see
// forecastClient.js).

import { VOIDED_CONTRACT_STATUS } from "./contractStatus.js";
import { fmtPct } from "./formatPct.js";

// Leading `MW:` (case-insensitive) followed by optional whitespace. Anchored at the
// start so an `MW:` occurring mid-sentence is never treated as the trigger.
const MW_PREFIX_RE = /^\s*MW:\s*/i;

/**
 * Does this text carry the leading `MW:` trigger?
 * @param {string} text
 * @returns {boolean}
 */
export function isMultiwayPrefixed(text) {
  return typeof text === "string" && MW_PREFIX_RE.test(text);
}

/**
 * Split an `MW:`-prefixed prompt into `{ isMultiway, question }`. When the prefix is
 * present, `question` is the remaining text with the prefix stripped; otherwise
 * `isMultiway` is false and `question` is the input trimmed of surrounding whitespace.
 * @param {string} text
 * @returns {{ isMultiway: boolean, question: string }}
 */
export function parseMultiwayPrefix(text) {
  const s = typeof text === "string" ? text : "";
  if (MW_PREFIX_RE.test(s)) {
    return { isMultiway: true, question: s.replace(MW_PREFIX_RE, "").trim() };
  }
  return { isMultiway: false, question: s.trim() };
}

// Leading `B:` (case-insensitive) followed by optional whitespace — the force-BINARY
// override, symmetric to `MW:`. With automatic question-type routing on, an unprefixed
// prompt is classified by the contract server (it may come back multi-valued); a user who
// wants to pin the ordinary yes/no path prefixes with `B:`. Anchored at the start so a
// `B:` mid-sentence is never the trigger.
const BINARY_PREFIX_RE = /^\s*B:\s*/i;

/**
 * Does this text carry the leading `B:` force-binary trigger?
 * @param {string} text
 * @returns {boolean}
 */
export function isBinaryPrefixed(text) {
  return typeof text === "string" && BINARY_PREFIX_RE.test(text);
}

/**
 * Split a `B:`-prefixed prompt into `{ isBinary, question }`. When the prefix is present,
 * `question` is the remaining text with the prefix stripped; otherwise `isBinary` is false
 * and `question` is the input trimmed of surrounding whitespace.
 * @param {string} text
 * @returns {{ isBinary: boolean, question: string }}
 */
export function parseBinaryPrefix(text) {
  const s = typeof text === "string" ? text : "";
  if (BINARY_PREFIX_RE.test(s)) {
    return { isBinary: true, question: s.replace(BINARY_PREFIX_RE, "").trim() };
  }
  return { isBinary: false, question: s.trim() };
}

/**
 * Question-type routing: which preprocess endpoint authors the contract, and the question
 * text with any honoured prefix stripped (`forced` says one was). Precedence: `MW:` (only
 * when `multiwayEnabled`) beats `B:` beats auto (when `autoQuestionTypeEnabled`) beats plain
 * binary; an unhonoured `MW:` stays in the text. Shared by submit and edit-resubmit so an edited question routes
 * by the same rule as the original (bug e87437b1).
 * @param {string} rawInput
 * @param {{ multiwayEnabled?: boolean, autoQuestionTypeEnabled?: boolean } | null} [flags]
 * @returns {{ mode: "binary" | "auto" | "multi", question: string, forced: boolean }}
 */
export function routePreprocess(rawInput, flags) {
  const { multiwayEnabled, autoQuestionTypeEnabled } = flags || {};
  const raw = typeof rawInput === "string" ? rawInput.trim() : "";
  const mw = parseMultiwayPrefix(raw);
  if (multiwayEnabled && mw.isMultiway)
    return { mode: "multi", question: mw.question, forced: true };
  const bin = parseBinaryPrefix(raw);
  if (bin.isBinary) return { mode: "binary", question: bin.question, forced: true };
  return { mode: autoQuestionTypeEnabled ? "auto" : "binary", question: raw, forced: false };
}

/**
 * The preprocess panel's edit buffer: the question behind the prefix its routing honoured
 * (`routing.forced`), so an edit keeps a `B:` / `MW:` override and deleting the prefix is
 * how to drop it.
 * @param {string} questionText
 * @param {{ forced?: string | null } | null} [routing]
 * @returns {string}
 */
export function editBufferOf(questionText, routing) {
  const text = typeof questionText === "string" ? questionText : "";
  if (routing?.forced === "binary") return `B: ${text}`;
  if (routing?.forced === "multi") return `MW: ${text}`;
  return text;
}

/**
 * Extract the multi-value answer from a forecast result, if present. Returns the PMF
 * masses paired with their render-ready bucket labels (index-aligned by construction),
 * or `null` for an ordinary binary forecast. Reads `forecast_value` +
 * `outcome_labels` off either the top-level result or its embedded `contract` (the
 * server writes both places; the contract copy is what survives a reload).
 * @param {any} src a forecast result or a contract object
 * @returns {{ buckets: Array<{ label: string, p: number }>, kind: string } | null}
 */
// The magnitude suffixes, largest first. Deliberately stops at B: a numeric forecast bin
// past 10^12 is vanishingly rare and "1.2T" is less readable than the grouped digits.
const _MAGNITUDES = [
  [1e9, "B"],
  [1e6, "M"],
  [1e3, "k"],
];

// Smallest magnitude we will abbreviate at. Below this, grouped digits are CLEARER than a
// suffix ("1,500" beats "1.5k") — and, load-bearing, it keeps 4-digit YEARS intact: a bin
// edge of 2027 must never render as "2k".
const _ABBREV_FLOOR = 10000;

// How much precision an abbreviation may carry. One decimal keeps "22.5M" but refuses
// "12.345M" — past that the suffix is hiding digits rather than saving them, so we fall back
// to grouped digits and lose nothing.
const _ABBREV_MAX_DECIMALS = 1;

/**
 * Abbreviate a round number to a magnitude suffix, or null when that would lose detail.
 *
 * The test is exactness, not size: the value must divide into at most
 * `_ABBREV_MAX_DECIMALS` decimal places. So 15000 → "15k" and 22500000 → "22.5M", while
 * 15234 and 12345000 return null and keep their digits. That is the whole point — an
 * abbreviation the reader cannot invert is worse than a long number.
 */
function abbreviateRound(n) {
  const abs = Math.abs(n);
  if (abs < _ABBREV_FLOOR) return null;
  for (const [scale, suffix] of _MAGNITUDES) {
    if (abs < scale) continue;
    const scaled = n / scale;
    const rounded = Number(scaled.toFixed(_ABBREV_MAX_DECIMALS));
    if (rounded !== scaled) return null; // would hide digits — refuse
    return `${rounded}${suffix}`;
  }
  return null;
}

/** Group an integer part with thousands separators. */
function groupDigits(intPart) {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// Format a bucket-edge number for display: abbreviate when it is round enough to stay
// lossless, else trim trailing zeros and group the digits.
function fmtEdge(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return String(n);
  const abbrev = abbreviateRound(n);
  if (abbrev) return abbrev;
  const plain = Number(n.toFixed(2)).toString();
  const [intPart, decPart] = plain.split(".");
  const neg = intPart.startsWith("-");
  const digits = neg ? intPart.slice(1) : intPart;
  // 4-digit integers stay UNSEPARATED — the same exemption `formatBucketLabel` has always
  // applied, and it exists for years: a bin edge of 2027 must not render as "2,027".
  const grouped = (neg ? "-" : "") + (digits.length >= 5 ? groupDigits(digits) : digits);
  return decPart != null ? `${grouped}.${decPart}` : grouped;
}

// Normalize percentage-synonym units to "%" so a numeric space authored with any of
// "percentage points" / "percent" / "pp" / … renders compactly as "0% to 10%" (the LLM is
// inconsistent about which it writes). Non-percentage units pass through unchanged.
const _PCT_UNITS = new Set([
  "%",
  "percent",
  "percentage",
  "percentages",
  "percentage point",
  "percentage points",
  "pp",
  "ppt",
  "ppts",
  "pct",
  "pts",
]);
function normUnit(unit) {
  if (typeof unit !== "string") return unit;
  return _PCT_UNITS.has(unit.trim().toLowerCase()) ? "%" : unit;
}

// The integer grid: a count cut at k + 0.5 (every achievable count strictly inside a bin) is
// spelled by the whole values each bin holds — "7", "7 to 8", "6 or fewer", "10 or more" —
// mirroring sooth_idl.bin_labels' rule: every edge a half-integer except that an OUTER edge
// may be the whole value it was clipped to, and at least one bin a full step of the grid
// (which keeps [0, 0.5, 1], a share rather than a count, in the range form).
const _GRID_EPS = 1e-9;
const _isHalfInteger = (v) => Math.abs(v - Math.floor(v) - 0.5) < _GRID_EPS;
const _isInteger = (v) => Math.abs(v - Math.round(v)) < _GRID_EPS;
function _isIntegerGrid(edges) {
  if (!edges.slice(1, -1).every(_isHalfInteger)) return false;
  for (const outer of [edges[0], edges[edges.length - 1]]) {
    if (!(_isHalfInteger(outer) || _isInteger(outer))) return false;
  }
  return edges.some(
    (e, i) => i < edges.length - 1 && _isHalfInteger(e) && _isHalfInteger(edges[i + 1]),
  );
}

/**
 * Human-readable labels for a numeric outcome space's bins, in PMF index order (open-low
 * tail first, interior bins, open-high tail last — matching the canonical labeller's
 * order/count so they stay index-aligned to the mass). Replaces the technical
 * "[0, 10)" / "< -20" / ">= 20" form used for scoring with a readable one:
 *   • interior bins → "0% to 10%"
 *   • open-low tail → "Below -20%"
 *   • open-high tail → "20% or more"
 * A compact unit ("%", "$", or a single glyph) hugs each number; a word unit ("seats") is
 * appended once per range ("0 to 10 seats").
 * @param {{edges:number[], open_low?:boolean, open_high?:boolean, edge_owners?:("lower"|"upper")[]}} binning
 * @param {string|null|undefined} unit
 * @returns {string[]}
 */
export function numericBucketLabels(binning, rawUnit) {
  const unit = normUnit(rawUnit);
  const edges = Array.isArray(binning?.edges) ? binning.edges : [];
  if (edges.length < 2) return [];
  const compact = unit === "%" || unit === "$" || (typeof unit === "string" && unit.length === 1);
  const wordUnit = unit && !compact ? ` ${unit}` : "";
  const n = (v) => {
    const s = fmtEdge(v);
    if (!compact) return s;
    return unit === "$" ? `$${s}` : `${s}${unit}`;
  };
  if (binning.edge_owners != null) {
    const owners = effectiveEdgeOwners(binning);
    const labels = [];
    if (binning.open_low)
      labels.push(`${owners[0] === "lower" ? "At most" : "Below"} ${n(edges[0])}${wordUnit}`);
    for (let i = 0; i < edges.length - 1; i++) {
      labels.push(
        `${owners[i] === "upper" ? "[" : "("}${n(edges[i])}, ${n(edges[i + 1])}${owners[i + 1] === "lower" ? "]" : ")"}${wordUnit}`,
      );
    }
    if (binning.open_high)
      labels.push(
        `${owners[owners.length - 1] === "upper" ? "At least" : "Above"} ${n(edges[edges.length - 1])}${wordUnit}`,
      );
    return labels;
  }
  const out = [];
  if (_isIntegerGrid(edges)) {
    if (binning.open_low) {
      out.push(`${n(Math.ceil(edges[0] - _GRID_EPS) - 1)} or fewer${wordUnit}`);
    }
    const last = edges.length - 2;
    for (let i = 0; i < edges.length - 1; i++) {
      const closed = i === last && !binning.open_high;
      const lo = Math.ceil(edges[i] - _GRID_EPS);
      const hi = closed
        ? Math.floor(edges[i + 1] + _GRID_EPS)
        : Math.ceil(edges[i + 1] - _GRID_EPS) - 1;
      out.push(lo === hi ? `${n(lo)}${wordUnit}` : `${n(lo)} to ${n(hi)}${wordUnit}`);
    }
    if (binning.open_high) {
      out.push(`${n(Math.ceil(edges[edges.length - 1] - _GRID_EPS))} or more${wordUnit}`);
    }
    return out;
  }
  if (binning.open_low) out.push(`Below ${n(edges[0])}${wordUnit}`);
  for (let i = 0; i < edges.length - 1; i++) {
    out.push(`${n(edges[i])} to ${n(edges[i + 1])}${wordUnit}`);
  }
  if (binning.open_high) out.push(`${n(edges[edges.length - 1])} or more${wordUnit}`);
  return out;
}

// The shapes numericBucketLabels writes: a figure (with a "$" or a one-glyph unit hugging
// it), then an optional word unit ("seats"), whose only digits are glued to a letter ("CO2").
const _FIG = String.raw`-?\$?-?\d[\d,]*(?:\.\d+)?[kKMBT]?[^\s\d,)\]]?`;
const _WORDS = String.raw`(?:\s+[^\d\s](?:[^\d]|(?<=[A-Za-z])\d)*)?`;
const _NUMERIC_LABEL = new RegExp(
  [
    `${_FIG}`,
    `${_FIG} to ${_FIG}`,
    String.raw`[[(]${_FIG}, ${_FIG}[)\]]`,
    `(?:Below|At most|Above|At least) ${_FIG}`,
    `${_FIG} or (?:fewer|more)`,
  ]
    .map((shape) => `^${shape}${_WORDS}$`)
    .join("|"),
);
/** Whether a bucket label is one `numericBucketLabels` writes (a range, a value or a tail). */
const isNumericBucketLabel = (label) => _NUMERIC_LABEL.test(String(label).trim());

/**
 * A numeric outcome's display label for its canonical key, as `sooth_idl.bin_labels` spells it
 * — "[2.5, 3)" → "2.5% to 3%", "< 2.5" → "Below 2.5%", ">= 5" → "5% or more", "(2.5, 3]" and
 * "<= 2.5" / "> 5" under edge owners → "(2.5%, 3%]" / "At most 2.5%" / "Above 5%", and the
 * integer grid's "7–8" / "<= 6" → "7 to 8" / "6 or fewer" — matched against `buckets` (whose
 * labels `numericBucketLabels` wrote) on the kind of range and every one of its figures. Keys
 * that already name a bucket, or that match none exactly, come back unchanged, so a range from
 * another binning is never read as one of these.
 * @param {string} key
 * @param {Array<{label: string}>|null|undefined} buckets
 * @returns {string}
 */
export function intervalDisplayLabel(key, buckets) {
  const k = String(key ?? "").trim();
  const labels = (Array.isArray(buckets) ? buckets : [])
    .map((b) => b?.label)
    .filter((l) => typeof l === "string");
  if (!labels.length || labels.includes(k)) return k;
  // Only a numeric space: every label must be one numericBucketLabels writes. A named outcome
  // that merely holds a number ("Not before 2030") turns the matching off.
  if (!labels.every(isNumericBucketLabel)) return k;
  // What a display label names, by how numericBucketLabels words it.
  const kindOf = (l) =>
    /^Below\b/.test(l)
      ? "<"
      : /^At most\b/.test(l) || /\bor fewer\b/.test(l)
        ? "<="
        : /^Above\b/.test(l)
          ? ">"
          : /^At least\b/.test(l) || /\bor more\b/.test(l)
            ? ">="
            : "range";
  // A label's figures as `numericBucketLabels` wrote them ("$1.5M" → "1.5M"), in order.
  const figures = (l) =>
    // A digit glued to a letter belongs to the unit ("CO2"), not a figure.
    (l.match(/(?<![A-Za-z\d.])-?\$?-?\d[\d,]*(?:\.\d+)?[kKMBT]?/g) || []).map((n) =>
      n.replace(/[$,]/g, ""),
    );
  const fig = (v) => fmtEdge(Number(v)).replace(/,/g, "");
  const brackets = (l) => {
    const m = /^([[(]).*([)\]])(?:\s+\S.*)?$/.exec(l.trim());
    return m ? m[1] + m[2] : null;
  };
  // A figure's value, its magnitude suffix included ("20k" → 20000).
  const SCALE = { k: 1e3, K: 1e3, M: 1e6, B: 1e9, T: 1e12 };
  const valueOf = (f) => {
    const m = /^(-?[\d.]+)([kKMBT]?)$/.exec(f);
    return m ? Number(m[1]) * (SCALE[m[2]] || 1) : NaN;
  };
  const upper = (l) => valueOf(figures(l)[1]);
  // A plain "a to b" label is the legacy half-open "[a, b)", or "[a, b]" for the top bin when
  // no high tail follows; an edge-owner label spells its own brackets.
  const hasHighTail = labels.some((l) => kindOf(l) === ">=" || kindOf(l) === ">");
  const topRange = labels
    .filter((l) => kindOf(l) === "range" && brackets(l) == null && figures(l).length === 2)
    .reduce((top, l) => (top == null || upper(l) > upper(top) ? l : top), null);
  const legacyEnds = (l) => (!hasHighTail && l === topRange ? "[]" : "[)");
  // A figure matches a key's number as written, or at the 6 significant digits the canonical
  // labeller (`%g`) keeps ("1.23457e+06" is "1,234,567").
  const sig6 = (v) => Number(Number(v).toPrecision(6));
  const same = (f, v) => f === fig(v) || sig6(valueOf(f)) === sig6(v);
  const find = (kind, nums, ends = null) => {
    const hits = labels.filter(
      (l) =>
        kindOf(l) === kind &&
        figures(l).length === nums.length &&
        figures(l).every((n, i) => same(n, nums[i])) &&
        // Ownership too: a key's brackets must match the label's (spelled or implied).
        (ends == null || (brackets(l) ?? legacyEnds(l)) === ends),
    );
    // Labels too coarse to tell bins apart ("0 to 0" twice) name none of them.
    return hits.length === 1 ? hits[0] : k;
  };
  const NUM = String.raw`(-?\d+(?:\.\d+)?(?:e[+-]?\d+)?)`;
  let m = new RegExp(String.raw`^(<=|<|>=|>)\s*${NUM}$`).exec(k);
  if (m) return find(m[1], [m[2]]);
  m = new RegExp(String.raw`^([[(])\s*${NUM}\s*,\s*${NUM}\s*([)\]])$`).exec(k);
  if (m) return find("range", [m[2], m[3]], m[1] + m[4]);
  // The integer grid: a lone value ("7") or a run of them ("7–8").
  m = new RegExp(String.raw`^${NUM}(?:\s*[–—-]\s*${NUM})?$`).exec(k);
  if (m) return find("range", m[2] != null ? [m[1], m[2]] : [m[1]]);
  return k;
}

// Ordinal lexicon: labels drawn from a KNOWN ranked scale. If a distribution's labels are
// mostly from one of these, the set has an inherent order (like numeric bins) and must NOT be
// re-ranked by probability. Lowercase, trimmed; compared with _isOrderedCategorical below.
const _ORDINAL_TERMS = new Set([
  // direction-of-change
  "decrease",
  "decline",
  "fall",
  "lower",
  "down",
  "no change",
  "unchanged",
  "stable",
  "flat",
  "same",
  "hold",
  "increase",
  "rise",
  "grow",
  "higher",
  "up",
  "much lower",
  "much higher",
  "slightly lower",
  "slightly higher",
  "worse",
  "better",
  "much worse",
  "much better",
  // magnitude / severity
  "negligible",
  "minimal",
  "very low",
  "low",
  "minor",
  "mild",
  "moderate",
  "medium",
  "elevated",
  "high",
  "very high",
  "severe",
  "critical",
  "extreme",
  "major",
  "none",
  "serious",
  // agreement
  "strongly disagree",
  "disagree",
  "neutral",
  "agree",
  "strongly agree",
  // frequency / likelihood
  "never",
  "rarely",
  "seldom",
  "sometimes",
  "occasionally",
  "often",
  "frequently",
  "always",
  "impossible",
  "unlikely",
  "possible",
  "probable",
  "likely",
  "certain",
  // quality / rating
  "poor",
  "fair",
  "average",
  "good",
  "very good",
  "great",
  "excellent",
  "outstanding",
  // market sentiment
  "bearish",
  "bullish",
]);

// True when a CATEGORICAL space's labels carry an inherent order (numbers, years, dates/periods,
// process stages, or an ordinal scale) and so should stay in authored order rather than be ranked
// by probability. Numeric-KIND spaces are handled by their kind and never reach here.
function _isOrderedCategorical(labels) {
  if (!Array.isArray(labels) || labels.length < 2) return false;
  const norm = labels.map((l) => String(l).trim().toLowerCase());
  // (a) every label is a plain number / count / year / decade ("2026", "1990s", "-3", "2.5")
  if (norm.every((l) => /^-?\d+(\.\d+)?$/.test(l) || /^\d{4}s$/.test(l))) return true;
  // (b) dates / time periods: ISO ("2026-01"), quarter ("q1 2026"), month name, bare year
  const MONTH = /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/;
  if (
    norm.every(
      (l) => /\d{4}-\d{1,2}/.test(l) || /^q[1-4]\b/.test(l) || MONTH.test(l) || /^\d{4}\b/.test(l),
    )
  ) {
    return true;
  }
  // (c) process stages / graded levels: "phase 2", "stage 3", "tier 1", "level 4", "step 2"
  if (norm.every((l) => /^(phase|stage|tier|level|step|round|grade|class|band|wave)\b/.test(l))) {
    return true;
  }
  // (d) ordinal lexicon: a MAJORITY of labels are recognized ranked-scale terms
  const hits = norm.filter((l) => _ORDINAL_TERMS.has(l)).length;
  return hits >= Math.max(2, Math.ceil(labels.length / 2));
}

/**
 * Order the outcome buckets for display: an UNORDERED (nominal) categorical is ranked by
 * probability, highest first, which reads best at a glance; an ORDERED space — numeric bins,
 * dates/periods, process stages, or an ordinal scale — keeps its natural authored order, where
 * the sequence itself carries meaning. Pure; returns a new array.
 * @param {Array<{label:string,p:number}>} buckets in authored (PMF index) order
 * @param {string} kind the outcome-space kind ("numeric" | "categorical" | …)
 * @returns {Array<{label:string,p:number}>}
 */
export function orderBuckets(buckets, kind) {
  // Numeric bins are authored ASCENDING (the edge array's own order). Displayed top-to-bottom
  // that reads smallest-first, which inverts how people scan a magnitude scale — so reverse
  // it: biggest number at the top. `numericBucketLabels` builds "Below X" first and
  // "X or more" last, so reversing also lands those two on the correct ends.
  if (kind === "numeric") return [...buckets].reverse();
  if (_isOrderedCategorical(buckets.map((b) => b.label))) return buckets;
  return [...buckets].sort((a, b) => (b.p || 0) - (a.p || 0));
}

export function extractMultiway(src) {
  if (!src || typeof src !== "object") return null;
  const fv = src.forecast_value || src.contract?.forecast_value;
  const labels = src.outcome_labels || src.contract?.outcome_labels;
  const mass = fv?.representation?.mass;
  if (!Array.isArray(mass) || mass.length === 0) return null;
  const space = fv?.outcome_space;
  const kind = space?.kind || "categorical";
  // Layer 1 Phase 3 (spec 2026-08-06-layer1-distribution-migration): a BooleanSpace is a
  // BINARY forecast expressed as its two-slot PMF, not a multiway one. Binary rows have
  // carried a `forecast_value` since Phase 1 and the whole historical corpus was
  // backfilled, so the forecast server is about to start surfacing it on the binary wire
  // path too — and this function discriminated on the FIELD'S PRESENCE, never on the
  // space's kind. Without this guard every binary forecast would render as an "Outcome
  // distribution" bar chart labelled "Outcome 1 / Outcome 2" (a BooleanSpace carries no
  // `labels`, so the generic fallback wins) in place of its P(YES) headline, and
  // `multiwaySummaryText` would post that instead of the binary summary.
  //
  // Deliberately keyed on `kind === "boolean"` rather than `mass.length === 2`: a
  // genuinely categorical two-outcome question is multiway and must keep rendering as
  // one. This is a NO-OP until the server populates the field for binary — which is why
  // it ships, and deploys, FIRST.
  if (kind === "boolean") return null;
  // Numeric: humanize the bin labels for DISPLAY ("0% to 10%" not "[0, 10)"), derived from
  // the space's binning + unit in the same index order as the canonical labels. Categorical
  // labels are already human, so keep them. Fall back to the backend labels (then generic)
  // if the derived count doesn't line up with the mass.
  let displayLabels = Array.isArray(labels) ? labels : null;
  if (kind === "numeric" && space?.binning) {
    const human = numericBucketLabels(space.binning, space.unit);
    if (human.length === mass.length) displayLabels = human;
  }
  // CATEGORICAL: the space NAMES ITS OWN OUTCOMES, so derive from it when the backend
  // sent no `outcome_labels` — the same move the numeric branch above makes with
  // `space.binning`, and the difference between a card reading "Blue ✓" and one reading
  // "Outcome 2 ✓" on a question whose answers were authored as Red/Blue/Green.
  //
  // Two spellings are accepted because two are in circulation: `labels` is canonical
  // (`sooth_idl.CategoricalSpace`, and its TS mirror), while the contract wire that
  // reaches this client carries `outcomes`. Reading only one silently keeps the generic
  // fallback for half the corpus, which is exactly the failure being fixed.
  //
  // A categorical space with `allow_other` has an IMPLICIT trailing slot; `sooth_idl`
  // spells it lowercase "other" and puts it last, so the derived list must too or every
  // label after it is off by one. The length guard is what enforces that: a derived list
  // that doesn't match the mass is discarded rather than mis-paired.
  if (!displayLabels && kind !== "numeric") {
    const named = Array.isArray(space?.labels)
      ? space.labels
      : Array.isArray(space?.outcomes)
        ? space.outcomes
        : null;
    if (named) {
      const withOther = space?.allow_other ? [...named, "other"] : named;
      if (withOther.length === mass.length) displayLabels = withOther;
    }
  }
  const buckets = mass.map((p, i) => ({
    label:
      displayLabels && typeof displayLabels[i] === "string" ? displayLabels[i] : `Outcome ${i + 1}`,
    p: typeof p === "number" ? p : 0,
    // The PMF slot this bucket weighs — the resolver's `outcome_index`. orderBuckets re-sorts
    // for display, so position in the returned array is NOT the slot; match on this instead.
    outcomeIndex: i,
    // The implicit catch-all is identified by SLOT, not by spelling: `allow_other` always
    // appends it last, and a label can be re-spelled by the backend.
    ...(kind !== "numeric" && space?.allow_other === true && i === mass.length - 1
      ? { isOther: true }
      : {}),
  }));
  // Nominal categoricals rank by probability; ordered spaces keep authored order (see orderBuckets).
  return { buckets: orderBuckets(buckets, kind), kind };
}

// ── ALL MULTIWAY (chatboard-all-multiway) ────────────────────────────────────
// One UX regardless of question type: with the flag on, a BINARY forecast renders
// through the same distribution surface as a multi-value one — neutral triggers on
// the spine and a two-bar breakdown in place of the P(YES) pie. The helpers below
// are what make a binary contract look like a distribution to the render layer; the
// components downstream then have a single shape to draw.

/** Bucket label for the YES side of a binary contract rendered as a distribution. */
export const BINARY_YES_LABEL = "Yes";
/** Bucket label for the NO side of a binary contract rendered as a distribution. */
export const BINARY_NO_LABEL = "No";

/**
 * Project a binary P(YES) onto the two-bucket distribution the multiway surface draws.
 * Accepts either a 0–1 probability or a 0–100 percentage (the wire has carried both).
 * Index order is fixed — YES first — because the resolution collapse below maps a
 * `resolved_yes` / `resolved_no` verdict onto these positions.
 * @param {number} probability
 * @returns {Array<{label: string, p: number}> | null} null when not a finite number
 */
export function binaryBuckets(probability) {
  if (typeof probability !== "number" || !Number.isFinite(probability)) return null;
  const p = probability > 1 ? probability / 100 : probability;
  const yes = Math.max(0, Math.min(1, p));
  return [
    { label: BINARY_YES_LABEL, p: yes },
    { label: BINARY_NO_LABEL, p: 1 - yes },
  ];
}

/**
 * The distribution to render for a forecast, whatever its question type — the single
 * entry point the timeline and the report modal both call.
 *
 * A genuine multi-value contract returns its PMF (`extractMultiway`). A binary one
 * returns the synthesized two-bucket projection, but ONLY under All Multiway: with the
 * flag off this returns null for binary and every caller falls through to the ordinary
 * pie path, so the flag is a true kill switch rather than a style tweak.
 *
 * `probability` is passed in rather than dug out of `src` because the two call sites
 * hold it in different shapes (the lane has `contract.probability`, the report modal has
 * an already-resolved `prob` that accounts for scrubbing).
 *
 * @param {any} src a forecast result or a contract object
 * @param {{allMultiway?: boolean, probability?: number|null}} [opts]
 * @returns {{ buckets: Array<{label: string, p: number}>, kind: string } | null}
 */
export function extractDistribution(src, { allMultiway = false, probability = null } = {}) {
  const mw = extractMultiway(src);
  if (mw) return mw;
  if (!allMultiway) return null;
  const buckets = binaryBuckets(probability);
  return buckets ? { buckets, kind: "binary" } : null;
}

/** Resolution statuses that name a settled outcome (a tie settles, but names no winner). */
const TERMINAL_STATUSES = ["resolved_yes", "resolved_no", "resolved_tie"];
/** The subset that names WHICH outcome won — i.e. TERMINAL_STATUSES minus the tie. */
const WINNER_NAMING_STATUSES = ["resolved_yes", "resolved_no"];

/**
 * The support slot a resolution NAMES, or null when it names none.
 *
 * Shared because three surfaces ask it and they must agree: the winner lookup below, the
 * bar collapse in `applyResolutionToDistribution`, and the chart's terminal-verdict gate.
 *
 * `Number.isInteger`, never truthiness: slot 0 is a real outcome (on a space with an open
 * low tail it is the entire lower tail), and `0 || null` is `null`.
 *
 * @param {{resolved_outcome?: {outcome_index?: number}}|null|undefined} resolution
 * @returns {number|null}
 */
export function namedOutcomeIndex(resolution) {
  const idx = resolution?.resolved_outcome?.outcome_index;
  return Number.isInteger(idx) && idx >= 0 ? idx : null;
}

/**
 * The bucket LABEL a resolution names, trimmed, or null. The one reading of these two
 * fields, so the winner lookup and the terminal gates cannot disagree about what counts.
 *
 * @param {any} resolution
 * @returns {string|null}
 */
function namedOutcomeLabel(resolution) {
  const label = resolution?.resolved_bucket_label ?? resolution?.resolved_outcome;
  return typeof label === "string" && label.trim() ? label.trim() : null;
}

/**
 * Does this resolution name an outcome at all — by slot or by label?
 *
 * The bucket-free terminal test, for callers that hold a verdict but no distribution (the
 * chart's verdict gate). `applyResolutionToDistribution` asks the stronger, bucket-
 * validated question by delegating to :func:`resolvedBucketIndex` directly.
 *
 * @param {any} resolution
 * @returns {boolean}
 */
export function namesAnOutcome(resolution) {
  return namedOutcomeIndex(resolution) !== null || namedOutcomeLabel(resolution) !== null;
}

/**
 * Is this a settled MULTI-VALUE verdict — one that names an outcome under no competing
 * status token?
 *
 * The notifier persists a categorical/numeric resolution as `resolved_outcome` with a null
 * status (the yes/no/tie vocabulary cannot say which of K outcomes occurred); the on-demand
 * route spells the same thing `resolved_multi`. Either is terminal. Any OTHER status wins
 * over a stray slot — `unresolved` especially, matching `applyResolutionToDistribution` and
 * `isConcluded`.
 *
 * @param {any} resolution
 * @returns {boolean}
 */
export function isNamedOutcomeVerdict(resolution) {
  const status = resolution?.status;
  return (!status || status === "resolved_multi") && namesAnOutcome(resolution);
}

/**
 * Which bucket a resolution says occurred, or null when nothing names one.
 *
 * BINARY is fully determined: `resolved_yes` is bucket 0 and `resolved_no` is bucket 1,
 * by the index order `binaryBuckets` fixes.
 *
 * MULTI-VALUE is named by the resolution itself, and needs no status: a bucket LABEL
 * (matched case-insensitively) or `resolved_outcome.outcome_index`, whichever it carries.
 * Null when it carries neither — the collapse is driven by a named outcome, never guessed.
 *
 * The index is matched against each bucket's own `outcomeIndex`, never against its
 * POSITION: buckets arrive in display order, so a positional read would mark the wrong one.
 *
 * @param {Array<{label: string}>} buckets
 * @param {string} kind the outcome-space kind ("binary" | "categorical" | "numeric")
 * @param {string|null|undefined} status a resolution status
 * @param {any} [resolution] the full resolution payload, for the multi-value fields
 * @returns {number|null}
 */
export function resolvedBucketIndex(buckets, kind, status, resolution) {
  if (!Array.isArray(buckets) || buckets.length === 0) return null;
  // A TIE settles the question but names no winner, whatever the outcome space — so it
  // is rejected here for every kind, before any per-kind mapping. (Callers reaching this
  // through `applyResolutionToDistribution` never get here on a tie, which handles it
  // first; this keeps the function honest for a direct caller.)
  if (status === "resolved_tie" || status === "unresolved") return null;
  // BINARY is decided by the status token and nothing else: the buckets ARE the verdicts,
  // so without a winner-naming status there is no winner. Unchanged.
  if (kind === "binary") {
    if (!WINNER_NAMING_STATUSES.includes(status)) return null;
    return status === "resolved_yes" ? 0 : 1;
  }
  // MULTI-VALUE does NOT require a status token, and that is the point: its buckets are
  // outcomes rather than verdicts, so the answer is WHICH SLOT — a thing the yes/no/tie
  // vocabulary cannot say. Requiring one is what kept a resolved multi-value forecast
  // rendering as though it had never settled.
  //
  // Two ways to name the slot, tried in order. A LABEL first, matched case-insensitively.
  // `extractMultiway` hands back buckets in DISPLAY order (`orderBuckets` ranks a nominal
  // categorical by probability), so neither path may use a bucket's POSITION — that would
  // address the PMF's order and silently mark the WRONG bucket. A label survives the
  // reordering; so does the slot each bucket carries, which is what the index path below
  // matches on.
  const label = namedOutcomeLabel(resolution);
  if (label !== null) {
    // Exact first; a case-folded match only when unambiguous, so an authored "Other" and the
    // implicit catch-all "other" are never mistaken for each other (the index path decides).
    const exact = buckets.findIndex((b) => String(b.label).trim() === label.trim());
    if (exact >= 0) return exact;
    const want = label.trim().toLowerCase();
    const folded = buckets.flatMap((b, i) =>
      String(b.label).trim().toLowerCase() === want ? [i] : [],
    );
    if (folded.length === 1) return folded[0];
  }
  // Labels are display text and get re-spelled (an integer grid reads "7", not "[6.5, 7.5)");
  // the resolver's outcome_index names the PMF slot whatever the spelling of the day. Buckets
  // arrive in DISPLAY order (orderBuckets), so match on the slot each bucket carries rather
  // than by position — a bucket without one (a foreign shape) is never guessed at.
  const idx = namedOutcomeIndex(resolution);
  if (idx !== null) {
    const at = buckets.findIndex((b) => b.outcomeIndex === idx);
    if (at >= 0) return at;
  }
  return null;
}

/**
 * Apply a resolution verdict to a distribution, for the timeline marker and the report.
 *
 * On a settled outcome the bars COLLAPSE to 100/0 — the outcome that occurred takes the
 * whole mass — and `resolvedIndex` flags the row that gets the check mark. A binary tie
 * collapses to an even 50/50 (matching the 0.5 the pie shows today) and names no winner.
 * Unresolved leaves the forecast bars standing: there is no outcome to collapse onto, so
 * showing one would be a lie; only the badge changes.
 *
 * Returns the input buckets unchanged (and a null badge) when there is no verdict yet,
 * so a live forecast passes through untouched.
 *
 * @param {{buckets: Array<{label: string, p: number}>, kind: string}} dist
 * @param {{status?: string|null, resolution?: any}} [verdict]
 * @returns {{buckets: Array<{label: string, p: number}>, resolvedIndex: number|null, badge: string|null, kind: string, status: string|null}}
 */
export function applyResolutionToDistribution(dist, { status = null, resolution = null } = {}) {
  const buckets = Array.isArray(dist?.buckets) ? dist.buckets : [];
  const kind = dist?.kind || "categorical";
  // Voided wins over any resolution payload, so it is answered before `namesAnOutcome`:
  // a void names no slot, and a verdict that named one would collapse the bars.
  if (status === VOIDED_CONTRACT_STATUS) {
    return { buckets, resolvedIndex: null, badge: "Voided", kind, status };
  }
  // A MULTI-VALUE verdict settles WITHOUT a status token — it names an outcome, which the
  // yes/no/tie vocabulary cannot express (see resolvedBucketIndex). So "names an outcome"
  // is a second, independent way for this to be terminal, and every status test below has
  // to admit it or the bars never collapse on a resolved multi-value forecast.
  //
  // Asked by DELEGATING to the same lookup that picks the winner, rather than by
  // re-testing one field: the lookup accepts a LABEL as well as an index, so a narrower
  // gate here would drop a label-named verdict the lookup could have resolved — the two
  // must admit exactly the same set or the gate silently vetoes a real winner. It also
  // validates against THESE buckets, so an outcome naming none of them is not terminal.
  // Binary is unaffected: it never names an outcome this way.
  const namesAnOutcome =
    kind !== "binary" && resolvedBucketIndex(buckets, kind, status, resolution) !== null;
  // `kind` and `status` ride out on every return so a consumer can tell WHICH verdict
  // settled it. The marker no longer styles on them — the winning row is gold either way —
  // but the report and the card still state the verdict in words, and it is cheaper to
  // carry than to re-derive.
  if ((!status && !namesAnOutcome) || buckets.length === 0)
    return { buckets, resolvedIndex: null, badge: null, kind, status };
  // An explicit `unresolved` wins over a stray slot: the resolver saying "not yet" is a
  // statement about the whole question, and collapsing the bars under it would show an
  // outcome the resolver declined to assert.
  // No "Unresolved" label either — on an open question it is no news, and past the
  // date the header says "No answer yet".
  if (status === "unresolved") return { buckets, resolvedIndex: null, badge: null, kind, status };
  if (status && !TERMINAL_STATUSES.includes(status))
    return { buckets, resolvedIndex: null, badge: null, kind, status };
  if (status === "resolved_tie") {
    const even =
      kind === "binary" ? buckets.map((b) => ({ ...b, p: 1 / buckets.length })) : buckets;
    return { buckets: even, resolvedIndex: null, badge: "Tied", kind, status };
  }
  const at = resolvedBucketIndex(buckets, kind, status, resolution);
  if (at == null) return { buckets, resolvedIndex: null, badge: "Resolved", kind, status };
  return {
    buckets: buckets.map((b, i) => ({ ...b, p: i === at ? 1 : 0 })),
    resolvedIndex: at,
    badge: "Resolved",
    kind,
    status,
  };
}

/**
 * A human-readable completion summary for a multi-value forecast — the per-bucket
 * breakdown as markdown lines, highest-probability first. Posted into the chat on
 * completion (the same slot binary forecasts post `report.forecast_summary`), so the
 * user sees the distribution inline and gets the "Full Report →" opener into the modal's
 * bar chart. Returns `null` for a binary/non-multi contract (caller falls back).
 * @param {any} src a forecast result or a contract object
 * @returns {string | null}
 */
export function multiwaySummaryText(src) {
  const mw = extractMultiway(src);
  if (!mw) return null;
  // Use the SAME display order as the bars (extractMultiway already ranked nominal categoricals
  // by probability and kept ordered spaces in authored order) so the chat summary matches the chart.
  const lines = mw.buckets.map((b) => `- **${bucketLabel(b)}** — ${bucketPctText(b.p)}`);
  return `Forecast distribution:\n\n${lines.join("\n")}`;
}

/**
 * A bucket probability as display text, by the same rule as a binary forecast (`fmtPct`):
 * a whole percent, one decimal below 1% and above 99%.
 * @param {number|null|undefined} p probability in 0..1
 * @returns {string}
 */
export function bucketPctText(p) {
  return `${fmtPct(p)}%`;
}

/**
 * Insert thousands separators into large numbers inside a bucket label, so a numeric
 * distribution's bin edges read "10,000" / "[10,000, 20,000)" instead of "10000" / "[10000,
 * 20000)". Only integer parts of 5+ digits are grouped: a 4-digit number is left bare so a
 * "which year" distribution's labels stay "2026", not "2,026". Signs, decimals, and
 * surrounding punctuation/units (%, brackets, unit suffixes) are preserved; categorical
 * labels with no long numbers pass through unchanged.
 * @param {string} label
 * @returns {string}
 */
export function formatBucketLabel(label) {
  return String(label ?? "").replace(/\d+(?:\.\d+)?/g, (num) => {
    const [intPart, decPart] = num.split(".");
    // 4-digit integers (years, and any value < 10,000) stay unseparated. Numbers that
    // arrive already abbreviated by `fmtEdge` ("22.5M") never reach here as bare digits,
    // so this only groups what stayed long.
    const grouped = intPart.length >= 5 ? groupDigits(intPart) : intPart;
    return decPart != null ? `${grouped}.${decPart}` : grouped;
  });
}

/** How the catch-all bucket is shown wherever a bucket label is rendered. */
export const OTHER_DISPLAY_LABEL = "Other (not listed)";

/**
 * A bucket's display label. The catch-all is named by its `isOther` SLOT flag, never by
 * spelling, so an authored outcome that happens to be called "Other" keeps its own name.
 *
 * @param {{label?: string, isOther?: boolean}|null|undefined} bucket
 * @returns {string}
 */
export function bucketLabel(bucket) {
  return bucket?.isOther === true ? OTHER_DISPLAY_LABEL : formatBucketLabel(bucket?.label);
}

/**
 * The highest-mass row that is not the catch-all; ties keep the earlier row. Falls back to
 * the catch-all only when nothing else exists. Rows without an `isOther` flag (binary,
 * numeric, foreign shapes) are all named, so for them this is the plain argmax.
 *
 * @param {Array<{label?: string, p?: number, isOther?: boolean}>|undefined|null} rows
 * @returns {object|null}
 */
export function topNamedRow(rows) {
  if (!rows?.length) return null;
  const named = rows.filter((r) => r?.isOther !== true);
  const pool = named.length ? named : rows;
  return pool.reduce((a, b) => ((b.p || 0) > (a.p || 0) ? b : a), pool[0]);
}

/**
 * The row a live distribution LEADS with.
 *
 * The catch-all is a bundle of every unlisted outcome, so out-massing any ONE named row
 * does not make it the leader — the top named row leads. It leads only when it outweighs
 * all named rows combined, i.e. when "something not listed" is the likelier answer.
 *
 * @param {Array<{label?: string, p?: number, isOther?: boolean}>|undefined|null} rows
 * @returns {object|null}
 */
export function leadingRow(rows) {
  const top = topNamedRow(rows);
  if (!top || top.isOther === true) return top;
  const other = rows.find((r) => r?.isOther === true);
  if (!other) return top;
  const namedMass = rows.reduce((s, r) => (r?.isOther === true ? s : s + (r?.p || 0)), 0);
  return (other.p || 0) > namedMass ? other : top;
}

/**
 * The row a distribution leads with: the outcome that OCCURRED once a resolution names one,
 * else {@link leadingRow}. Returns `resolvedRow` alongside it because callers need to know
 * WHICH of those two cases they are in — the marker draws a check mark, the readout picks a
 * badge. Shared so the highlighted bar and anything restating it cannot drift apart.
 *
 * Keep in lockstep with the server's `leadingBucket` (server/lib/notify/leadingBin.js), so an
 * email and the chart never name different leaders.
 *
 * @param {Array<{label?: string, p?: number, isOther?: boolean}>|undefined|null} rows
 * @param {number|null|undefined} resolvedIndex
 * @returns {{resolvedRow: object|null, lead: object|null}}
 */
export function selectLead(rows, resolvedIndex) {
  if (!rows?.length) return { resolvedRow: null, lead: null };
  const resolvedRow =
    typeof resolvedIndex === "number" && rows[resolvedIndex] ? rows[resolvedIndex] : null;
  return { resolvedRow, lead: resolvedRow || leadingRow(rows) };
}

/**
 * The winning row of a resolved-or-not distribution, as the hover readout states it.
 *
 * Reads the winner off `selectLead`, the SAME call MultiwayMarker highlights a row with, so
 * a readout and the bar cannot name different buckets — one implementation, not two that
 * agree today. Returns null when there is nothing to state.
 *
 * @param {{buckets: Array<{label?: string, p?: number}>, resolvedIndex?: number|null, badge?: string|null}|null} dist
 * @returns {{label: string, pct: number, pctText: string, badge: string}|null}
 */
export function winnerOf(dist) {
  const { resolvedRow, lead } = selectLead(dist?.buckets, dist?.resolvedIndex);
  if (!lead) return null;
  return {
    label: bucketLabel(lead),
    pct: Math.round((lead.p || 0) * 100),
    pctText: bucketPctText(lead.p),
    // The distribution's own badge wins when it has one — it carries "Tied"/"Unresolved",
    // which neither fallback below could reconstruct.
    badge: dist.badge || (resolvedRow ? "Resolved" : "Most likely"),
  };
}

/**
 * A distribution with its catch-all ("Other (not listed)") moved to the end, for the report:
 * ranked by probability, a large catch-all would otherwise top the named outcomes. The
 * resolved row is followed to its new position. Pure; returns the input when there is nothing
 * to move.
 * @param {{buckets: Array<{isOther?: boolean}>, resolvedIndex?: number|null}|null} dist
 */
export function withOtherLast(dist) {
  const buckets = dist?.buckets;
  if (!Array.isArray(buckets) || !buckets.some((b) => b?.isOther)) return dist;
  const ordered = [...buckets.filter((b) => !b?.isOther), ...buckets.filter((b) => b?.isOther)];
  const resolved = typeof dist.resolvedIndex === "number" ? buckets[dist.resolvedIndex] : undefined;
  return {
    ...dist,
    buckets: ordered,
    resolvedIndex:
      resolved === undefined ? (dist.resolvedIndex ?? null) : ordered.indexOf(resolved),
  };
}
